import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Play, X } from 'lucide-react';
import { Button, Modal } from '@vd/ui';
import { getWalkthroughVideo, WALKTHROUGH_VIDEO_KEY } from './walkthroughVideoApi';
import styles from './WalkthroughVideo.module.css';

/**
 * "Watch the walkthrough" on Home (TOADO-983, Jason): a still with a play
 * button that opens the video in a large modal, rather than a small player in
 * the column. Which video it is comes from Admin, so it changes without a
 * deploy. The player is mounted only while the modal is open, so closing it
 * stops the video.
 */
export function WalkthroughVideo() {
  const { data } = useQuery({ queryKey: WALKTHROUGH_VIDEO_KEY, queryFn: getWalkthroughVideo, staleTime: 5 * 60_000 });
  const [open, setOpen] = useState(false);
  // The still that failed to load, so a video set later still shows its own.
  const [failedStill, setFailedStill] = useState<string | null>(null);
  const posterRef = useRef<HTMLButtonElement>(null);
  const video = data?.video;
  const still = video?.thumbnailUrl && video.thumbnailUrl !== failedStill ? video.thumbnailUrl : null;
  // Focus goes back to the play button as the dialog closes (returnFocusRef, TOADO-984).
  const close = () => setOpen(false);
  return (
    <>
      <button
        ref={posterRef}
        type="button"
        className={styles.poster}
        onClick={() => setOpen(true)}
        disabled={!video}
        aria-label="Play the walkthrough video"
        data-testid="walkthrough-video-open"
      >
        {still
          ? <img className={styles.still} src={still} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailedStill(still)} />
          : <span className={styles.blank} aria-hidden />}
        <span className={styles.play} aria-hidden><Play size={22} fill="currentColor" /></span>
      </button>
      <Modal open={open && !!video} onClose={close} width={1120} labelledBy="walkthrough-video-title" returnFocusRef={posterRef}>
        <div className={styles.dialog}>
          <div className={styles.head}>
            <h2 id="walkthrough-video-title" className={styles.title}>Toado walkthrough</h2>
            <Button size="sm" variant="ghost" iconOnly leftIcon={<X size={16} aria-hidden />} aria-label="Close the video" onClick={close} />
          </div>
          <div className={styles.frame}>
            {open && video && (
              <iframe
                src={video.embedUrl}
                title="Toado walkthrough video"
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                data-testid="walkthrough-video-player"
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
